import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { ICE_COLORS, ICE_TETROMINO_COLORS, getBlockColor } from '../CyanMaterial';

// Ice shard that shoots upward and spins
const IceShard = ({ startPosition, velocity, rotationSpeed, size, delay, tint }) => {
  const meshRef = useRef();
  const startTime = useRef(null);

  const material = useMemo(() => new THREE.MeshBasicMaterial({
    color: new THREE.Color(tint),
    transparent: true,
    opacity: 0.9,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  }), [tint]);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0) {
      meshRef.current.visible = false;
      return;
    }

    if (adjustedTime > 1.5) {
      meshRef.current.visible = false;
      return;
    }

    meshRef.current.visible = true;

    const t = adjustedTime;
    const gravity = -18;

    // Position with physics - ice shards float more
    const x = startPosition[0] + velocity[0] * t;
    const y = startPosition[1] + velocity[1] * t + 0.5 * gravity * t * t;
    const z = startPosition[2] + velocity[2] * t;

    meshRef.current.position.set(x, y, z);

    // Tumbling rotation
    meshRef.current.rotation.x += rotationSpeed[0] * 0.08;
    meshRef.current.rotation.y += rotationSpeed[1] * 0.08;
    meshRef.current.rotation.z += rotationSpeed[2] * 0.08;

    // Fade out with shimmer
    const shimmer = Math.sin(adjustedTime * 20) * 0.15 + 0.85;
    const fadeProgress = Math.min(adjustedTime / 1.2, 1);
    meshRef.current.material.opacity = 0.9 * (1 - fadeProgress) * shimmer;
  });

  // Create elongated crystal/shard shape
  const geometry = useMemo(() => {
    const geo = new THREE.ConeGeometry(size * 0.3, size, 4);
    geo.rotateX(Math.PI); // Point downward initially
    return geo;
  }, [size]);

  return (
    <mesh ref={meshRef} visible={false} geometry={geometry}>
      <primitive object={material} attach="material" />
    </mesh>
  );
};

// Freezing wave that sweeps across the rows
const FreezeSweep = ({ row, delay, direction }) => {
  const meshRef = useRef();
  const startTime = useRef(null);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > 0.5) {
      meshRef.current.visible = false;
      return;
    }

    meshRef.current.visible = true;

    const progress = adjustedTime / 0.5;

    // Sweep across the row
    const xPos = direction * (-6 + progress * 12);
    meshRef.current.position.x = xPos;

    // Leading edge bright, trailing fade
    const intensity = Math.sin(progress * Math.PI) * 0.9;
    meshRef.current.material.opacity = intensity;
    meshRef.current.scale.x = 2 + Math.sin(progress * Math.PI) * 1;
  });

  const yPos = 20 - row - 0.5;

  return (
    <mesh ref={meshRef} position={[0, yPos, 0.3]} visible={false}>
      <planeGeometry args={[2, 1.1]} />
      <meshBasicMaterial
        color={ICE_COLORS.frost}
        transparent
        opacity={0.9}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </mesh>
  );
};

// Crystallization effect on a block before it shatters
const CrystallizingBlock = ({ position, color, delay }) => {
  const meshRef = useRef();
  const crackRef = useRef();
  const startTime = useRef(null);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    // Phase 1: Freeze flash (0-0.15s)
    // Phase 2: Crack appearance (0.15-0.3s)
    // Phase 3: Shatter and disappear (0.3s+)

    if (adjustedTime <= 0) {
      meshRef.current.visible = false;
      if (crackRef.current) crackRef.current.visible = false;
      return;
    }

    if (adjustedTime > 0.35) {
      meshRef.current.visible = false;
      if (crackRef.current) crackRef.current.visible = false;
      return;
    }

    meshRef.current.visible = true;

    if (adjustedTime < 0.15) {
      // Freeze flash phase
      const freezeProgress = adjustedTime / 0.15;
      const flash = Math.sin(freezeProgress * Math.PI);
      meshRef.current.material.emissiveIntensity = 2 * flash;
      meshRef.current.material.opacity = 0.9;
      if (crackRef.current) crackRef.current.visible = false;
    } else if (adjustedTime < 0.3) {
      // Crack phase
      const crackProgress = (adjustedTime - 0.15) / 0.15;
      meshRef.current.material.emissiveIntensity = 0.5;
      meshRef.current.material.opacity = 0.85;

      if (crackRef.current) {
        crackRef.current.visible = true;
        crackRef.current.material.opacity = crackProgress * 0.9;
      }
    } else {
      // Shatter phase - quick fade
      const shatterProgress = (adjustedTime - 0.3) / 0.05;
      meshRef.current.material.opacity = 0.85 * (1 - shatterProgress);
      if (crackRef.current) crackRef.current.visible = false;
    }
  });

  return (
    <group position={position}>
      {/* The freezing block */}
      <mesh ref={meshRef} visible={false}>
        <boxGeometry args={[0.9, 0.9, 0.4]} />
        <meshStandardMaterial
          color={color}
          emissive={ICE_COLORS.frost}
          emissiveIntensity={0.5}
          transparent
          opacity={0.9}
          metalness={0.9}
          roughness={0.1}
        />
      </mesh>

      {/* Crack overlay */}
      <mesh ref={crackRef} position={[0, 0, 0.21]} visible={false}>
        <planeGeometry args={[0.85, 0.85]} />
        <meshBasicMaterial
          color={ICE_COLORS.crystal}
          transparent
          opacity={0}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
};

// Snowflake particle drifting upward
const SnowflakeParticle = ({ startPosition, delay }) => {
  const meshRef = useRef();
  const startTime = useRef(null);
  const drift = useMemo(() => ({
    x: (Math.random() - 0.5) * 2,
    speed: 3 + Math.random() * 2,
    spin: (Math.random() - 0.5) * 5
  }), []);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > 2) {
      meshRef.current.visible = false;
      return;
    }

    meshRef.current.visible = true;

    const progress = adjustedTime / 2;

    // Drift upward with slight horizontal wobble
    const wobble = Math.sin(adjustedTime * 3) * 0.3;
    meshRef.current.position.x = startPosition[0] + wobble + drift.x * progress;
    meshRef.current.position.y = startPosition[1] + adjustedTime * drift.speed;
    meshRef.current.position.z = startPosition[2];

    // Spin slowly
    meshRef.current.rotation.z += drift.spin * 0.016;

    // Fade in then out
    const fadeIn = Math.min(progress * 5, 1);
    const fadeOut = progress > 0.7 ? 1 - (progress - 0.7) / 0.3 : 1;
    meshRef.current.material.opacity = 0.7 * fadeIn * fadeOut;
    meshRef.current.scale.setScalar(0.12 * fadeIn);
  });

  return (
    <mesh ref={meshRef} position={startPosition} visible={false}>
      <circleGeometry args={[0.12, 6]} />
      <meshBasicMaterial
        color={ICE_COLORS.snow}
        transparent
        opacity={0.7}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </mesh>
  );
};

// Large central frost nova
const FrostNova = () => {
  const ringRef = useRef();
  const innerRef = useRef();
  const startTime = useRef(null);

  useFrame((state) => {
    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;

    // Delayed start for dramatic effect
    const adjustedTime = elapsed - 0.2;

    if (adjustedTime <= 0) {
      if (ringRef.current) ringRef.current.visible = false;
      if (innerRef.current) innerRef.current.visible = false;
      return;
    }

    if (adjustedTime > 0.7) {
      if (ringRef.current) ringRef.current.visible = false;
      if (innerRef.current) innerRef.current.visible = false;
      return;
    }

    const progress = adjustedTime / 0.7;

    // Expanding frost ring
    if (ringRef.current) {
      ringRef.current.visible = true;
      const scale = progress * 30;
      ringRef.current.scale.setScalar(scale);
      ringRef.current.material.opacity = 0.6 * (1 - progress);
    }

    // Inner frost glow
    if (innerRef.current) {
      innerRef.current.visible = progress < 0.4;
      const innerProgress = progress / 0.4;
      innerRef.current.scale.setScalar(1 + innerProgress * 8);
      innerRef.current.material.opacity = 0.8 * (1 - innerProgress);
    }
  });

  return (
    <>
      <mesh ref={ringRef} position={[0, 10, 0.4]} visible={false}>
        <ringGeometry args={[0.85, 1, 6]} />
        <meshBasicMaterial
          color={ICE_COLORS.frost}
          transparent
          opacity={0.6}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>
      <mesh ref={innerRef} position={[0, 10, 0.35]} visible={false}>
        <circleGeometry args={[0.8, 6]} />
        <meshBasicMaterial
          color={ICE_COLORS.crystal}
          transparent
          opacity={0.8}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
    </>
  );
};

// Vertical ice pillar that forms and shatters
const IcePillar = ({ x, delay }) => {
  const meshRef = useRef();
  const startTime = useRef(null);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > 0.6) {
      meshRef.current.visible = false;
      return;
    }

    meshRef.current.visible = true;

    const progress = adjustedTime / 0.6;

    if (progress < 0.3) {
      // Form phase - grow upward
      const formProgress = progress / 0.3;
      meshRef.current.scale.y = formProgress;
      meshRef.current.position.y = 10 - (1 - formProgress) * 10;
      meshRef.current.material.opacity = 0.4 * formProgress;
    } else {
      // Shatter phase - fade out
      const shatterProgress = (progress - 0.3) / 0.7;
      meshRef.current.scale.y = 1;
      meshRef.current.position.y = 10;
      meshRef.current.material.opacity = 0.4 * (1 - shatterProgress);
      // Slight outward push
      meshRef.current.position.x = x + (x > 0 ? 1 : -1) * shatterProgress * 0.5;
    }
  });

  return (
    <mesh ref={meshRef} position={[x, 10, 0.2]} visible={false}>
      <boxGeometry args={[0.15, 20, 0.1]} />
      <meshBasicMaterial
        color={ICE_COLORS.frost}
        transparent
        opacity={0.4}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </mesh>
  );
};

const IceTetrisFinisher = ({ onComplete, blocks = [] }) => {
  const groupRef = useRef();
  const startTime = useRef(Date.now());

  const allColors = useMemo(() => Object.values(ICE_TETROMINO_COLORS), []);

  // Get unique rows for effects
  const uniqueRows = useMemo(() => {
    if (blocks.length === 0) return [16, 17, 18, 19];
    const rows = [...new Set(blocks.map(b => b.row))];
    return rows.sort((a, b) => a - b);
  }, [blocks]);

  // Crystallizing blocks - show the freeze effect on each block
  const crystallizingBlocks = useMemo(() => {
    const sourceBlocks = blocks.length > 0 ? blocks : (() => {
      const fallback = [];
      for (let row = 16; row < 20; row++) {
        for (let col = 0; col < 10; col++) {
          fallback.push({
            row,
            col,
            colorClass: allColors[Math.floor(Math.random() * allColors.length)]
          });
        }
      }
      return fallback;
    })();

    return sourceBlocks.map((block) => {
      const x = block.col - 4.5;
      const y = 20 - block.row - 0.5;
      const color = typeof block.colorClass === 'string'
        ? getBlockColor(block.colorClass)
        : block.colorClass;

      // Stagger the freeze effect from center outward
      const distFromCenter = Math.abs(block.col - 4.5) / 5;
      const delay = distFromCenter * 0.15;

      return { position: [x, y, 0.2], color, delay };
    });
  }, [blocks, allColors]);

  // Ice shards that burst upward after crystallization
  const iceShards = useMemo(() => {
    const shards = [];
    const sourceBlocks = blocks.length > 0 ? blocks : (() => {
      const fallback = [];
      for (let row = 16; row < 20; row++) {
        for (let col = 0; col < 10; col++) {
          fallback.push({ row, col });
        }
      }
      return fallback;
    })();

    sourceBlocks.forEach((block) => {
      const baseX = block.col - 4.5;
      const baseY = 20 - block.row - 0.5;

      // 2-3 shards per block
      const shardCount = 2 + Math.floor(Math.random() * 2);

      for (let s = 0; s < shardCount; s++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 4 + Math.random() * 6;
        const upwardBias = 10 + Math.random() * 8;

        const vx = Math.cos(angle) * speed + (baseX * 0.8);
        const vy = upwardBias;
        const vz = (Math.random() - 0.5) * 3;

        const rx = (Math.random() - 0.5) * 10;
        const ry = (Math.random() - 0.5) * 10;
        const rz = (Math.random() - 0.5) * 10;

        // Delay matches crystallization + shatter timing
        const distFromCenter = Math.abs(block.col - 4.5) / 5;
        const delay = 0.3 + distFromCenter * 0.15 + Math.random() * 0.05;

        const size = 0.3 + Math.random() * 0.3;
        const tint = Math.random() > 0.5 ? ICE_COLORS.frost : ICE_COLORS.crystal;

        shards.push({
          startPosition: [baseX + (Math.random() - 0.5) * 0.3, baseY, 0.3],
          velocity: [vx, vy, vz],
          rotationSpeed: [rx, ry, rz],
          size,
          delay,
          tint,
        });
      }
    });

    return shards;
  }, [blocks]);

  // Freeze sweep effects across each row
  const freezeSweeps = useMemo(() => {
    const sweeps = [];
    uniqueRows.forEach((row, i) => {
      // Two sweeps per row from opposite directions
      sweeps.push({ row, delay: i * 0.04, direction: 1 });
      sweeps.push({ row, delay: i * 0.04 + 0.08, direction: -1 });
    });
    return sweeps;
  }, [uniqueRows]);

  // Snowflake particles floating upward
  const snowflakes = useMemo(() => {
    const particles = [];
    const count = 40;

    for (let i = 0; i < count; i++) {
      const row = uniqueRows[Math.floor(Math.random() * uniqueRows.length)];
      const x = (Math.random() - 0.5) * 12;
      const y = 20 - row - 0.5 + (Math.random() - 0.5) * 2;

      particles.push({
        startPosition: [x, y, 0.1],
        delay: 0.2 + Math.random() * 0.3,
      });
    }

    return particles;
  }, [uniqueRows]);

  // Ice pillars at edges
  const icePillars = useMemo(() => {
    return [
      { x: -5.5, delay: 0.1 },
      { x: 5.5, delay: 0.1 },
      { x: -4, delay: 0.15 },
      { x: 4, delay: 0.15 },
    ];
  }, []);

  useFrame(() => {
    const elapsed = (Date.now() - startTime.current) / 1000;

    if (elapsed > 2.2) {
      onComplete?.();
    }
  });

  return (
    <group ref={groupRef}>
      {/* Central frost nova */}
      <FrostNova />

      {/* Ice pillars at edges */}
      {icePillars.map((pillar, i) => (
        <IcePillar key={`pillar-${i}`} x={pillar.x} delay={pillar.delay} />
      ))}

      {/* Freeze sweeps across rows */}
      {freezeSweeps.map((sweep, i) => (
        <FreezeSweep
          key={`sweep-${i}`}
          row={sweep.row}
          delay={sweep.delay}
          direction={sweep.direction}
        />
      ))}

      {/* Crystallizing blocks */}
      {crystallizingBlocks.map((block, i) => (
        <CrystallizingBlock
          key={`crystal-${i}`}
          position={block.position}
          color={block.color}
          delay={block.delay}
        />
      ))}

      {/* Ice shards bursting upward */}
      {iceShards.map((shard, i) => (
        <IceShard
          key={`shard-${i}`}
          startPosition={shard.startPosition}
          velocity={shard.velocity}
          rotationSpeed={shard.rotationSpeed}
          size={shard.size}
          delay={shard.delay}
          tint={shard.tint}
        />
      ))}

      {/* Snowflakes drifting upward */}
      {snowflakes.map((flake, i) => (
        <SnowflakeParticle
          key={`snow-${i}`}
          startPosition={flake.startPosition}
          delay={flake.delay}
        />
      ))}
    </group>
  );
};

export default IceTetrisFinisher;
